import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Heading, PageLoader, Paragraph } from '@/components/atoms';
import { KYBForm } from './components/kyb-form';
import type {
  PersonalDetailsFormData,
  BusinessDetailsFormData,
} from '@/validation-schema';
import { useUser, useVerifyBusiness, useBusiness } from '@/api';
import { BUSINESS_QUERY_KEY } from '@/api/queries/business';
import type {
  RegisteredBusinessVerifyPayload,
  UnregisteredBusinessVerifyPayload,
} from '@/api/mutations/business';
import type { Business } from '@/types/business';
import { routes } from '@/lib/constants';
import {
  mapBusinessToAddressFormValues,
  mapBusinessToKybAddressPrefill,
} from '@/lib/helpers/business-address';

function isBusinessVerified(b: Business | null | undefined): boolean {
  if (!b) return false;
  return b.verificationStatus === 'verified';
}

function isBusinessReadonly(b: Business | null | undefined): boolean {
  if (!b) return false;
  return (
    b.verificationStatus === 'verified' || b.verificationStatus === 'pending'
  );
}

function mapBusinessToFormData(
  b: Business
): Partial<BusinessDetailsFormData & PersonalDetailsFormData> {
  const type = b.type ?? 'registered';
  const sd = b.submittedData;
  const address = mapBusinessToAddressFormValues(b);

  if (type === 'registered') {
    return {
      businessType: 'registered',
      businessName: sd?.businessName ?? b.name ?? '',
      rcNumber: sd?.idNumberMasked ?? b.idNumber ?? '',
      street: address.businessAddress,
      state: address.state,
      customState: address.customState,
      lga: address.lga,
      customLga: address.customLga,
      country: address.country,
      customCountry: address.customCountry,
      officeAddress: sd?.officeAddress ?? '',
      businessIndustry: sd?.industry ?? b.category ?? '',
      businessDescription: sd?.description ?? b.description ?? '',
    };
  }
  return {
    businessType: 'unregistered',
    firstName: sd?.firstName ?? '',
    lastName: sd?.lastName ?? '',
    middleName: sd?.middleName ?? '',
    dateOfBirth: sd?.dateOfBirth ?? '',
    emailAddress: sd?.email ?? '',
    phoneNumber: sd?.phoneNumber ?? '',
    nationality: sd?.nationality ?? b.country ?? '',
    nin: sd?.nin ?? b.idNumber ?? '',
  };
}

const MAX_VERIFICATION_ATTEMPTS = 3;

export const KYBPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [lockedAfterSubmit, setLockedAfterSubmit] = useState(false);
  const [initialPersonalData, setInitialPersonalData] = useState<
    Partial<PersonalDetailsFormData>
  >({});

  const {
    data: user,
    isLoading: isLoadingUser,
    isError: isUserError,
    error: userError,
    refetch: refetchUser,
  } = useUser('me');
  const {
    data: business,
    isLoading: isLoadingBusiness,
    isError: isBusinessError,
    error: businessError,
    refetch: refetchBusiness,
  } = useBusiness();
  const isLoading = isLoadingUser || isLoadingBusiness;
  const fetchError = isUserError || isBusinessError;
  const fetchErrorMessage =
    (userError instanceof Error ? userError.message : null) ??
    (businessError instanceof Error ? businessError.message : null) ??
    'Failed to load KYB information. Please try again.';

  const { mutate: verifyBusiness, isPending: isVerifying } = useVerifyBusiness(
    () => {
      setLockedAfterSubmit(true);
      queryClient.invalidateQueries({ queryKey: [BUSINESS_QUERY_KEY] });
      navigate(routes.root);
    }
  );

  const verified = useMemo(() => isBusinessVerified(business), [business]);
  const readonlyFromServer = useMemo(
    () => isBusinessReadonly(business),
    [business]
  );
  const readOnly = readonlyFromServer || lockedAfterSubmit;
  const initialBusinessData = useMemo(() => {
    if (!business) return undefined;
    if (readOnly) return mapBusinessToFormData(business);
    if (business.type === 'unregistered') return undefined;
    return mapBusinessToKybAddressPrefill(business);
  }, [business, readOnly]);

  const maxAttemptsReached =
    !!business &&
    !verified &&
    (business.verificationAttempts ?? 0) >= MAX_VERIFICATION_ATTEMPTS;

  useEffect(() => {
    if (user) {
      setInitialPersonalData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        middleName: user.otherName || '',
        dateOfBirth: user.dob || '',
        emailAddress: user.auth?.email || '',
        phoneNumber: user.whatsappNumber || '',
        nationality: user.country || '',
      });
    }
  }, [user]);

  useEffect(() => {
    if (
      business &&
      business.verificationStatus !== 'pending' &&
      business.verificationStatus !== 'verified'
    ) {
      setLockedAfterSubmit(false);
    }
  }, [business]);

  const handleSubmit = (
    payload: RegisteredBusinessVerifyPayload | UnregisteredBusinessVerifyPayload
  ) => {
    verifyBusiness(payload);
  };

  const handleRetry = () => {
    refetchUser();
    refetchBusiness();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-4 md:p-6 lg:p-8">
        <Heading size="xl">KYB Information</Heading>
        <PageLoader message="Loading KYB information..." />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col gap-6 p-4 md:p-6 lg:p-8">
        <Heading size="xl">KYB Information</Heading>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-center">
          <Paragraph className="text-destructive font-medium">
            {fetchErrorMessage}
          </Paragraph>
          <Button
            variant="outline"
            className="mt-3"
            onClick={handleRetry}
            label="Try again"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-col gap-2">
        <Heading size="xl">KYB Information</Heading>
        {verified && (
          <Paragraph className="text-sm text-green-600 font-medium">
            Your business is verified. Details below are read-only.
          </Paragraph>
        )}
        {!verified && business?.verificationStatus === 'pending' && (
          <Paragraph className="text-sm text-amber-700 font-medium">
            Your KYB details have been submitted and are currently under review.
            Details below are read-only.
          </Paragraph>
        )}
        {maxAttemptsReached && (
          <div className="rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 p-4">
            <Paragraph className="text-sm text-amber-800 dark:text-amber-200 font-medium">
              You have reached the maximum number of verification attempts (
              {MAX_VERIFICATION_ATTEMPTS}). Please contact support to proceed
              with business verification.
            </Paragraph>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm">
        <KYBForm
          key={readOnly ? 'readonly' : 'edit'}
          onSubmit={handleSubmit}
          initialPersonalData={initialPersonalData}
          initialBusinessData={initialBusinessData}
          disabled={readOnly}
          isVerifying={isVerifying}
          maxAttemptsReached={maxAttemptsReached}
        />
      </div>
    </div>
  );
};
